import { Injectable, signal } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  ACCOUNT_SYSTEM_ROLE_TEMPLATES,
  ORG_ROLES_SEED,
  PROPERTIES,
  accountSystemRoleId,
} from '@common/data';
import type { Role, RoleDraft, RolePage, RoleQuery, RoleScope } from '@common/types';

const LIST_LATENCY_MS = 150;
const SAVE_LATENCY_MS = 250;
const SYSTEM_ROLE_DATE = '2025-01-08T09:00:00.000Z';
const SYSTEM_ROLE_MEMBER_COUNTS = [1, 2, 3, 0];

function buildSystemRoles(): Role[] {
  const roles: Role[] = [];
  PROPERTIES.forEach((account, accountIndex) => {
    ACCOUNT_SYSTEM_ROLE_TEMPLATES.forEach((template, templateIndex) => {
      roles.push({
        id: accountSystemRoleId(template.id, account.id),
        scope: 'account',
        accountId: account.id,
        name: template.name,
        description: template.description,
        system: true,
        permissions: template.permissions,
        memberIds: seedMembers(accountIndex, templateIndex),
        createdAtIso: SYSTEM_ROLE_DATE,
        updatedAtIso: SYSTEM_ROLE_DATE,
      });
    });
  });
  return roles;
}

function seedMembers(accountIndex: number, templateIndex: number): string[] {
  const count = SYSTEM_ROLE_MEMBER_COUNTS[templateIndex] ?? 0;
  const start = (accountIndex * 5 + templateIndex) % 45;
  return Array.from({ length: count }, (_, i) => String(start + i + 1));
}

@Injectable({ providedIn: 'root' })
export class RolesService {
  private readonly roles = signal<Role[]>([
    ...ORG_ROLES_SEED.map((role) => ({ ...role })),
    ...buildSystemRoles(),
  ]);
  private readonly _revision = signal<number>(0);
  private seq = 0;

  readonly revision = this._revision.asReadonly();

  getById(id: string): Role | undefined {
    return this.roles().find((role) => role.id === id);
  }

  rolesByScope(scope: RoleScope, accountId: string | null = null): Role[] {
    return this.roles()
      .filter(
        (role) => role.scope === scope && (scope !== 'account' || role.accountId === accountId),
      )
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  list(scope: RoleScope, query: RoleQuery, accountId: string | null = null): Observable<RolePage> {
    let result = this.roles().filter((role) => role.scope === scope);
    if (scope === 'account') {
      result = result.filter((role) => role.accountId === accountId);
    }

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (role) =>
          role.name.toLowerCase().includes(term) ||
          role.description.toLowerCase().includes(term),
      );
    }

    const typeFilters = query.filters['type'];
    if (typeFilters?.length) {
      const allowSystem = typeFilters.includes('system');
      const allowCustom = typeFilters.includes('custom');
      result = result.filter((role) => (role.system ? allowSystem : allowCustom));
    }

    const field = query.sortField ?? 'name';
    const direction = query.sortOrder === 'desc' ? -1 : 1;
    result = result.slice().sort((a, b) => {
      const av = this.sortValue(a, field);
      const bv = this.sortValue(b, field);
      if (av < bv) return -1 * direction;
      if (av > bv) return 1 * direction;
      return 0;
    });

    const total = result.length;
    const start = Math.max(0, query.first);
    const end = start + Math.max(1, query.rows);
    const data = result.slice(start, end);

    return of({ data, total }).pipe(delay(LIST_LATENCY_MS));
  }

  create(scope: RoleScope, draft: RoleDraft, accountId: string | null = null): Observable<Role> {
    const name = draft.name.trim();
    if (!name) return throwError(() => new Error('Role name is required.'));
    const ownerId = scope === 'account' ? accountId : null;
    if (this.nameTaken(scope, ownerId, name, null)) {
      return throwError(() => new Error(`A role named "${name}" already exists.`));
    }
    const now = new Date().toISOString();
    const created: Role = {
      id: this.generateId(name),
      scope,
      accountId: ownerId,
      name,
      description: draft.description.trim(),
      system: false,
      permissions: { ...draft.permissions },
      memberIds: [],
      createdAtIso: now,
      updatedAtIso: now,
    };
    this.roles.update((rows) => [...rows, created]);
    this._revision.update((n) => n + 1);
    return of(created).pipe(delay(SAVE_LATENCY_MS));
  }

  update(id: string, draft: RoleDraft): Observable<Role> {
    const current = this.getById(id);
    if (!current) return throwError(() => new Error('Role not found.'));
    if (current.system) {
      return throwError(() => new Error('System roles cannot be edited — duplicate to customize.'));
    }
    const name = draft.name.trim();
    if (!name) return throwError(() => new Error('Role name is required.'));
    if (this.nameTaken(current.scope, current.accountId, name, id)) {
      return throwError(() => new Error(`A role named "${name}" already exists.`));
    }
    const next: Role = {
      ...current,
      name,
      description: draft.description.trim(),
      permissions: { ...draft.permissions },
      updatedAtIso: new Date().toISOString(),
    };
    this.commit(id, next);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  duplicate(id: string): Observable<Role> {
    const current = this.getById(id);
    if (!current) return throwError(() => new Error('Role not found.'));
    const name = this.uniqueCopyName(current.scope, current.accountId, current.name);
    const now = new Date().toISOString();
    const copy: Role = {
      ...current,
      id: this.generateId(name),
      name,
      system: false,
      permissions: { ...current.permissions },
      memberIds: [],
      createdAtIso: now,
      updatedAtIso: now,
    };
    this.roles.update((rows) => [...rows, copy]);
    this._revision.update((n) => n + 1);
    return of(copy).pipe(delay(SAVE_LATENCY_MS));
  }

  remove(id: string): Observable<void> {
    const current = this.getById(id);
    if (!current) return throwError(() => new Error('Role not found.'));
    if (current.system) {
      return throwError(() => new Error('System roles cannot be deleted.'));
    }
    if (current.memberIds.length > 0) {
      return throwError(
        () =>
          new Error(
            `Cannot delete: ${current.memberIds.length} member(s) still hold this role.`,
          ),
      );
    }
    this.roles.update((rows) => rows.filter((role) => role.id !== id));
    this._revision.update((n) => n + 1);
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  assignMembers(roleId: string, memberIds: readonly string[]): Observable<Role> {
    const current = this.getById(roleId);
    if (!current) return throwError(() => new Error('Role not found.'));
    const next: Role = {
      ...current,
      memberIds: [...memberIds],
      updatedAtIso: new Date().toISOString(),
    };
    this.commit(roleId, next);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  removeMember(roleId: string, memberId: string): Observable<Role> {
    const current = this.getById(roleId);
    if (!current) return throwError(() => new Error('Role not found.'));
    const next: Role = {
      ...current,
      memberIds: current.memberIds.filter((id) => id !== memberId),
      updatedAtIso: new Date().toISOString(),
    };
    this.commit(roleId, next);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  private commit(id: string, next: Role): void {
    this.roles.update((rows) => rows.map((role) => (role.id === id ? next : role)));
    this._revision.update((n) => n + 1);
  }

  private sortValue(role: Role, field: string): string | number {
    switch (field) {
      case 'permissions':
        return Object.keys(role.permissions).length;
      case 'members':
        return role.memberIds.length;
      case 'type':
        return role.system ? 'system' : 'custom';
      case 'updatedAtIso':
        return role.updatedAtIso;
      case 'description':
        return role.description.toLowerCase();
      default:
        return role.name.toLowerCase();
    }
  }

  private nameTaken(
    scope: RoleScope,
    accountId: string | null,
    name: string,
    excludeId: string | null,
  ): boolean {
    const lowered = name.toLowerCase();
    return this.roles().some(
      (role) =>
        role.scope === scope &&
        role.accountId === accountId &&
        role.id !== excludeId &&
        role.name.toLowerCase() === lowered,
    );
  }

  private uniqueCopyName(scope: RoleScope, accountId: string | null, baseName: string): string {
    let candidate = `${baseName} (Copy)`;
    let i = 2;
    while (this.nameTaken(scope, accountId, candidate, null)) {
      candidate = `${baseName} (Copy ${i})`;
      i += 1;
    }
    return candidate;
  }

  private generateId(name: string): string {
    this.seq += 1;
    return `role-custom-${this.slug(name)}-${Date.now().toString(36)}-${this.seq}`;
  }

  private slug(value: string): string {
    return (
      value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'role'
    );
  }
}
